import { memo, useEffect, useState, useTransition } from "react";
import { bool, number, string } from "prop-types";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { sharedProposal } from "@paths";
import { useCheckEstimateEditorAccess, useEditorWebsocket, useStores } from "@hooks";
import {
  useEstimateEditorSettings,
  useEstimateEditorStore,
  useEstimateStructure,
  useTooltipHoverActions
} from "@tools";
import { Button, ActionPopMenu, Alert, TextClearInput } from "@components";
import { CircularProgress, Grid, IconButton, Tooltip } from "@material-ui/core";
import { FileCopy, Pencil, Trash, Lock } from "@assets";
import { LockOpenOutlined, LockOutlined, Visibility, VisibilityOff } from "@material-ui/icons";
import useStyle from "./VersionButton.style";
import classnames from "classnames";

export const VersionButton = memo(({
  identifier,
  name,
  selected,
  editable,
  removable,
  hideable,
  clickable,
  isDragged,
  used,
  locked,
  editVisibility,
  rememberScroll,
  blockActionMenu,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { company, project, projectHash } = useParams();
  const { stateStore } = useStores();
  const structure = useEstimateStructure();
  const editorStore = useEstimateEditorStore();
  const socket = useEditorWebsocket();
  const classes = useStyle();
  
  const { isProposal } = useEstimateEditorSettings();
  
  const [ isPending, startTransition ] = useTransition()
  const [ alertVisible, showAlert ] = useState(false);
  const [ newName, setNewName ] = useState(name);
  const [ isEdited, setEditState ] = useState(false);
  const [ isSaving, setSavingState ] = useState(false);
  
  const { isOwner } = useCheckEstimateEditorAccess(editorStore.estimateUsers, editorStore.workTypesWithStatus);
  
  const disableInteraction = isEdited || isDragged || isSaving;

  const {
    anchor,
    hover,
    handleHoverIn,
    handleHoverOut,
    handlePopupClose,
    handleTimeoutClear,
  } = useTooltipHoverActions(disableInteraction);

  useEffect(() => {
    setNewName(name);
  }, [name]);

  const handleVersionChange = async () => {
    startTransition(async() => {
      if (selected || isSaving || !clickable) return;
      
      if(socket?.editionPrivileges) {
        socket.requestExistingVersion(identifier, rememberScroll);
        return;
      }
      // setSavingState(true);
      await editorStore.setCurrentVersion(identifier);
      if(isProposal) {
        navigate(sharedProposal(company, project, projectHash, identifier), { replace: true });
        if(rememberScroll)
          stateStore.scrollRef?.scrollTo({ top: editorStore.versionScroll[editorStore.currentVersionKey] || 0 });
      }
      // socket.navigateAndScroll(identifier, rememberScroll);
      // setSavingState(false);
    })
  }

  const handleVersionRemoval = async (e) => {
    e.stopPropagation();
    if (isSaving) return;
    showAlert(false);
    setSavingState(true);

    socket?.requestVersionRemoval(identifier);
  }

  const handleRemovalAlert = () => {
    if (isSaving) return;
    showAlert(true);
  }

  const handleVersionVisibility = () => {
    editorStore.setVersionHidden(identifier, !used);
    socket?.requestVersionVisibility(identifier, used ? 0 : 1);
    if(structure.visibility && typeof structure.visibility?.version === "boolean")
      structure.visibility.setVersionVisibility(undefined);
  }
  
  const handleVersionLock = () => {
    editorStore.setEditionLock(identifier, !locked);
    socket?.requestVersionLock(identifier, locked ? 0 : 1);
  }
  
  const handleEditState = () => {
    setEditState(true);
    handleHoverOut();
  }

  const handleNameChange = (v) => {
    setNewName(v.replace(/[\\^{}`]/g, ""));//.slice(0, 30));
  }

  const commitNewName = async () => {
    if (newName && newName !== name) {
      socket?.requestVersionRename(identifier, newName.trim());
      editorStore.editVersionName(identifier, newName.trim());
    } else setNewName(name);

    setEditState(false);
  }
  
  return (
    <Grid
      item container
      className={classnames(
      "w-max m-1",
        isEdited && "noDrag",
      )}
    >
      <Grid item container className="relative">
        <Button
          ref={anchor}
          variant="outlined"
          size="large"
          onClick={!disableInteraction ? handleVersionChange : undefined}
          onDoubleClick={
            editable && !disableInteraction ? handleEditState : undefined
          }
          icon={isSaving || isPending ? <CircularProgress size={24} /> : <FileCopy />}
          onMouseEnter={editable && !isDragged ? handleHoverIn : undefined}
          onMouseLeave={editable && !isDragged ? handlePopupClose : undefined}
          component="div"
          disableRipple={disableInteraction}
          disableFocusRipple
          className={classnames(
            "button-version",
            !selected && "preset-version",
            selected && "preset-version-active",
            selected && classes.selected,
            !used && editVisibility && classes.hidden
          )}
        >
          {
            isEdited
              ? <TextClearInput
                focused
                acceptOnClickAway
                confirmOnEnter
                value={newName}
                className={classes.nameInput}
                onChange={handleNameChange}
                onAccept={commitNewName}
                maxLength={50}
              />
             : <span>{name}</span>
          }
        </Button>
        <Grid item container className={classes.badgeContainer}>
          {
            editable && !disableInteraction && !editVisibility && !used &&
            <Tooltip title={t("views.editor.versions.hidden")} placement="right">
              <Grid
                item container
                alignItems="center"
                justifyContent="center"
                className={classes.offBadge}
              >
                <VisibilityOff className="text-md" />
              </Grid>
            </Tooltip>
          }
          {
            editable && !disableInteraction && locked &&
            <Tooltip title={t("views.editor.versions.locked")} placement="right">
              <Grid
                item container
                alignItems="center"
                justifyContent="center"
                className={classes.offBadge}
              >
                <Lock className="text-md" />
              </Grid>
            </Tooltip>
          }
        </Grid>
        {
          editable && !disableInteraction && editVisibility &&
          <Tooltip title={t(`views.editor.visibility.${used ? "hide" : "show"}_this`, {thing: t("views.editor.visibility.version")})}>
            <IconButton
              size="small"
              onClick={handleVersionVisibility}
              disabled={!hideable && used}
              className="absolute"
              style={{ bottom: -28, right: 4 }}
            >
              {
                used
                  ? <Visibility color={!hideable && used ? undefined : "primary"} />
                  : <VisibilityOff color={!hideable && used ? undefined : "primary"} />
              }
            </IconButton>
          </Tooltip>
        }
      </Grid>
      {
        editable && !disableInteraction && !editVisibility && !blockActionMenu &&
        <ActionPopMenu
          show={editVisibility ? true : hover}
          anchor={anchor?.current}
          onMouseEnter={handleTimeoutClear}
          onMouseLeave={handleHoverOut}
          placement="bottom-end"
        >
          <Button
            onClick={() => setEditState(true)}
            tooltipText={t("views.editor.versions.rename")}
          >
            <Pencil />
          </Button>
          {
            isOwner &&
            <Button
              onClick={handleVersionLock}
              tooltipText={t(`views.editor.versions.${locked ? "unlock" : "lock"}`)}
            >
              {
                locked ? <LockOpenOutlined /> : <LockOutlined />
              }
            </Button>
          }
          <Button
            onClick={handleRemovalAlert}
            disabled={!removable}
            tooltipText={t("views.editor.versions.remove")}
          >
            <Trash color={removable ? "error" : undefined} />
          </Button>
        </ActionPopMenu>
      }
      <Alert
        isOpen={alertVisible}
        width={440}
        title={<Grid item container justifyContent="center">
          <h2 className="text-lg">{t("alerts.editor.confirm_version_removal")}</h2>
          <h2 className="mt-2 text-lg">{name}</h2>
        </Grid>}
        onAccept={handleVersionRemoval}
        onCancel={() => showAlert(false)}
      />
    </Grid>
  );
});

VersionButton.propTypes = {
  identifier: number.isRequired,
  name: string.isRequired,
  selected: bool,
  editable: bool,
  removable: bool,
  hideable: bool,
  clickable: bool,
  isDragged: bool,
  used: bool,
  locked: bool,
  editVisibility: bool,
  rememberScroll: bool,
  blockActionMenu: bool,
};
